import React from "react"
import { Price, Subscription } from "@/types/billingPlans"
import { Progress, Typography } from "antd"
import { isEmpty, startCase } from "lodash"
import { DateTime } from "luxon"
import { Trans, useTranslation } from "react-i18next"
import { isCardDefaultPaymentMethodAtom } from "@/atoms/userAtom"
import { useRecoilValue } from "recoil"
import { Currency } from "@/types/currencies.types"

interface SubscriptionSummary {
  subscription?: Subscription
  currency: Currency
  price?: Price
}

const { Title } = Typography

const SubscriptionSummary: React.FC<SubscriptionSummary> = ({ subscription, price, currency }) => {
  const { t } = useTranslation()
  const isCardDefaultPayment = useRecoilValue(isCardDefaultPaymentMethodAtom)

  return (
    <div
      id="subscription-summary"
      className="flex flex-col w-full p-4 bg-kosmoGrayLight border rounded-md"
    >
      <Title level={5}>
        {t(
          subscription
            ? "profile.SubscriptionSummary.currentPlan"
            : "profile.SubscriptionSummary.noActiveSubscriptionTitle"
        )}
      </Title>
      <div className="flex flex-col gap-y-1">
        {subscription && !isEmpty(subscription?.product) && price ? (
          <>
            {subscription?.product?.orderQuotes > 0 && (
              <div className="flex w-1/2 gap-x-2">
                <Progress
                  type="line"
                  showInfo={false}
                  percent={
                    (subscription?.createdOrderInPeriod / subscription?.product?.orderQuotes) * 100
                  }
                  strokeColor="#4100FF"
                  trailColor="#FFFFFF"
                />
                <p className="whitespace-nowrap">
                  <span id="created-orders-in-period" className="text-kosmoPrimaryBlue">
                    {subscription?.createdOrderInPeriod}
                  </span>{" "}
                  / <span id="free-orders-subscription">{subscription?.product?.orderQuotes}</span>{" "}
                  {t("common.orders")}
                </p>
              </div>
            )}
            <p id="plan-name">
              <Trans>
                {t("profile.SubscriptionSummary.currentPlanDescription", {
                  planName:
                    isCardDefaultPayment && subscription?.product?.name
                      ? startCase(subscription?.product?.name?.split("(")?.[0]?.trim())
                      : "Custom",
                })}{" "}
                {subscription?.product?.orderQuotes > 0 &&
                  t("profile.SubscriptionSummary.currentPlanOrdersIncluded", {
                    freeOrders: subscription?.product?.orderQuotes,
                  })}{" "}
                {subscription?.product?.additionalOrderPrice > 0 &&
                  t("profile.SubscriptionSummary.additionalPricePerOrderDesc", {
                    currency: currency.symbol,
                    amount: (subscription?.product?.additionalOrderPrice / 100).toFixed(2),
                  })}
              </Trans>
            </p>
            {subscription?.product?.price?.amount > 0 && (
              <p>
                {subscription?.cancelAtPeriodEnd
                  ? t("profile.SubscriptionSummary.subscriptionEndsOn")
                  : t("profile.SubscriptionSummary.subscriptionNextPaymentAt")}{" "}
                <span className="text-[#EF4444]">
                  {DateTime.fromSeconds(subscription.endPeriod).toFormat("dd LLL yyyy")}
                </span>
                .
              </p>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-y-2">
            <p>{t("profile.SubscriptionSummary.noActiveSubscriptionDesc")}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default SubscriptionSummary
